import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";

import utils from "../../../universal/utils";
import { colors } from "../dark-hero/colors";
import {
  formatSignupRecency,
  isRecentEnoughSignup,
  selectQualifyingSignups,
} from "./recentSignupToast";
import { isPlausibleUsername } from "./plausibleUsername";

const MotionBox = motion(Box);

const SIGNUP_ACCENT = "#F0B458";
const SIGNUP_ACCENT_SOFT = "rgba(240, 180, 88, 0.18)";
const CARD_BG = "rgba(18, 18, 22, 0.96)";
const INITIAL_DELAY_MIN_MS = 5000;
const INITIAL_DELAY_MAX_MS = 10000;
const POLL_INTERVAL_MS = 30000;
const FIRST_POLL_DELAY_MS = 15000;
const DISPLAY_DURATION_MS = 6500;
const NEXT_CARD_DELAY_MS = 180;

function LiveIndicator() {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: SIGNUP_ACCENT_SOFT,
        flexShrink: 0,
        "&::after": {
          content: '""',
          position: "absolute",
          inset: "2px",
          borderRadius: "50%",
          backgroundColor: SIGNUP_ACCENT,
        },
      }}
    />
  );
}

function formatDisplayNickname(nickname) {
  const trimmed = String(nickname || "").trim();
  if (!trimmed) return "Player";
  if (trimmed.length <= 24) return trimmed;
  return `${trimmed.slice(0, 21)}…`;
}

function RecentSignupNotificationCard({ signup, onDismiss }) {
  const joinedLabel = signup?.joinedAt ? formatSignupRecency(signup.joinedAt) : null;
  const nickname = formatDisplayNickname(signup?.nickname);
  const countryName = signup?.countryName || "Unknown location";

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      onDismiss?.();
    }, DISPLAY_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [onDismiss, signup?.signupKey]);

  return (
    <MotionBox
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        pointerEvents: "auto",
        width: { xs: "min(20rem, calc(100vw - 1.5rem))", sm: "20rem" },
        px: 1.5,
        py: 1.25,
        borderRadius: "12px",
        border: `1px solid ${colors.border.default}`,
        backgroundColor: CARD_BG,
        boxShadow: "0 14px 36px rgba(0, 0, 0, 0.42)",
        backdropFilter: "blur(12px)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
        <LiveIndicator />
        <Typography
          sx={{
            fontSize: "0.62rem",
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: SIGNUP_ACCENT,
          }}
        >
          Just joined
        </Typography>
      </Box>

      <Typography
        sx={{
          fontSize: "0.92rem",
          fontWeight: 700,
          lineHeight: 1.35,
          color: colors.text.primary,
        }}
      >
        {nickname} just signed up!
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.65, minWidth: 0 }}>
        {signup?.flagImageUrl ? (
          <Box
            component="img"
            src={signup.flagImageUrl}
            alt=""
            sx={{
              width: 18,
              height: 13,
              borderRadius: "2px",
              objectFit: "cover",
              flexShrink: 0,
              border: `1px solid ${colors.border.subtle}`,
            }}
          />
        ) : null}
        <Typography
          sx={{
            fontSize: "0.78rem",
            lineHeight: 1.35,
            color: colors.text.secondary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          from {countryName}
        </Typography>
      </Box>

      {joinedLabel ? (
        <Typography
          sx={{
            mt: 0.55,
            fontSize: "0.72rem",
            color: colors.text.muted,
          }}
        >
          {joinedLabel}
        </Typography>
      ) : null}
    </MotionBox>
  );
}

RecentSignupNotificationCard.propTypes = {
  signup: PropTypes.shape({
    signupKey: PropTypes.string.isRequired,
    nickname: PropTypes.string,
    countryName: PropTypes.string,
    flagImageUrl: PropTypes.string,
    joinedAt: PropTypes.string,
  }).isRequired,
  onDismiss: PropTypes.func,
};

async function fetchRecentSignups(basePath, { since, limit = 10 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (since) params.set("since", since);

  const response = await fetch(`${basePath}/public/recent-signups?${params.toString()}`, {
    credentials: "omit",
  });
  if (!response.ok) return [];
  const payload = await response.json();
  return Array.isArray(payload?.signups) ? payload.signups : [];
}

export default function RecentSignupNotifications({ enabled = true }) {
  const [activeSignup, setActiveSignup] = React.useState(null);
  const queueRef = React.useRef([]);
  const queuedKeysRef = React.useRef(new Set());
  const shownKeysRef = React.useRef(new Set());
  const newestJoinedAtRef = React.useRef(null);
  const initialBurstDoneRef = React.useRef(false);
  const isShowingRef = React.useRef(false);
  const dismissDelayRef = React.useRef(null);
  const basePath = React.useMemo(() => utils.getEdgeBasePath(), []);

  const trackNewestJoinedAt = React.useCallback((signups) => {
    for (const signup of signups) {
      if (!signup?.joinedAt) continue;
      const joinedMs = new Date(signup.joinedAt).getTime();
      if (!Number.isFinite(joinedMs)) continue;

      const currentNewest = newestJoinedAtRef.current
        ? new Date(newestJoinedAtRef.current).getTime()
        : 0;
      if (joinedMs > currentNewest) {
        newestJoinedAtRef.current = signup.joinedAt;
      }
    }
  }, []);

  const enqueueSignups = React.useCallback(
    (signups, { skipShown = true } = {}) => {
      if (!Array.isArray(signups) || signups.length === 0) return [];

      trackNewestJoinedAt(signups);
      const qualifying = selectQualifyingSignups(signups);
      if (qualifying.length === 0) return [];

      const accepted = [];

      for (const signup of qualifying) {
        if (
          skipShown &&
          (shownKeysRef.current.has(signup.signupKey) ||
            queuedKeysRef.current.has(signup.signupKey))
        ) {
          continue;
        }
        queuedKeysRef.current.add(signup.signupKey);
        queueRef.current.push(signup);
        accepted.push(signup);
      }

      return accepted;
    },
    [trackNewestJoinedAt]
  );

  const showNext = React.useCallback(() => {
    if (isShowingRef.current) return;

    while (queueRef.current.length > 0) {
      const next = queueRef.current.shift();
      queuedKeysRef.current.delete(next?.signupKey);
      if (
        !next?.signupKey ||
        !isRecentEnoughSignup(next.joinedAt) ||
        !isPlausibleUsername(next.nickname)
      ) {
        continue;
      }

      isShowingRef.current = true;
      shownKeysRef.current.add(next.signupKey);
      setActiveSignup(next);
      return;
    }

    setActiveSignup(null);
  }, []);

  const handleDismiss = React.useCallback(() => {
    isShowingRef.current = false;
    setActiveSignup(null);
    if (dismissDelayRef.current) {
      window.clearTimeout(dismissDelayRef.current);
    }
    dismissDelayRef.current = window.setTimeout(() => {
      dismissDelayRef.current = null;
      showNext();
    }, NEXT_CARD_DELAY_MS);
  }, [showNext]);

  React.useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    const runInitialFetch = async () => {
      try {
        const signups = await fetchRecentSignups(basePath, { limit: 10 });
        if (cancelled || signups.length === 0) return;

        trackNewestJoinedAt(signups);
        enqueueSignups(signups, { skipShown: false });
      } catch {
        // Best-effort social proof.
      }
    };

    const runPoll = async ({ live = false } = {}) => {
      if (live && typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }

      try {
        const signups = await fetchRecentSignups(basePath, {
          since: newestJoinedAtRef.current,
          limit: 10,
        });
        if (cancelled || signups.length === 0) return;

        const accepted = enqueueSignups(signups);
        if (accepted.length > 0 && (live || initialBurstDoneRef.current)) {
          showNext();
        }
      } catch {
        // Ignore polling failures.
      }
    };

    runInitialFetch();

    const initialDelayMs =
      INITIAL_DELAY_MIN_MS +
      Math.floor(Math.random() * (INITIAL_DELAY_MAX_MS - INITIAL_DELAY_MIN_MS));
    const initialTimer = window.setTimeout(() => {
      if (cancelled) return;
      initialBurstDoneRef.current = true;
      showNext();
    }, initialDelayMs);

    const firstPollTimer = window.setTimeout(() => {
      if (!cancelled) runPoll({ live: true });
    }, FIRST_POLL_DELAY_MS);

    const pollTimer = window.setInterval(() => {
      runPoll({ live: true });
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(initialTimer);
      window.clearTimeout(firstPollTimer);
      window.clearInterval(pollTimer);
      if (dismissDelayRef.current) {
        window.clearTimeout(dismissDelayRef.current);
        dismissDelayRef.current = null;
      }
    };
  }, [basePath, enabled, enqueueSignups, showNext, trackNewestJoinedAt]);

  if (!enabled) return null;

  return (
    <Box
      aria-label="Recent signups"
      sx={{
        position: "fixed",
        left: { xs: "50%", sm: "auto" },
        right: { xs: "auto", sm: "1.25rem" },
        bottom: { xs: "1rem", sm: "1.25rem" },
        transform: { xs: "translateX(-50%)", sm: "none" },
        zIndex: 13000,
        pointerEvents: "none",
      }}
    >
      <AnimatePresence initial={false} mode="wait">
        {activeSignup ? (
          <RecentSignupNotificationCard
            key={activeSignup.signupKey}
            signup={activeSignup}
            onDismiss={handleDismiss}
          />
        ) : null}
      </AnimatePresence>
    </Box>
  );
}

RecentSignupNotifications.propTypes = {
  enabled: PropTypes.bool,
};
